import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { config } from '../../config/appConfig';
import { Game } from './TerrariaEngine/Game';

const Terraria2Game = memo(({ isActive = true, isFullscreen = false, onClose }) => {
    const gameRef = useRef(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [isLoadingTop, setIsLoadingTop] = useState(false);
    const saveScoreRef = useRef(null);
    const { isAuthenticated, token } = useAuth();
    const { t } = useLanguage();

    const fetchLeaderboard = useCallback(async () => {
        setIsLoadingTop(true);
        try {
            const response = await axios.get(`${config.api.baseUrl}/api/public/leaderboard/terraria`);
            if (response.data && response.data.success) setLeaderboard(response.data.data);
        } catch (error) {
            console.error('Failed to fetch terraria leaderboard:', error.response?.status, error.message);
        } finally { setIsLoadingTop(false); }
    }, []);

    const saveScore = useCallback(async (score) => {
        if (!isAuthenticated || !token || score <= 0) return;
        try {
            await axios.post(
                `${config.api.baseUrl}/api/account/score/terraria`,
                { score },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchLeaderboard();
        } catch (error) { console.error('[Terraria] Failed to save score:', error); }
    }, [isAuthenticated, token, fetchLeaderboard]);

    useEffect(() => { saveScoreRef.current = saveScore; }, [saveScore]);
    useEffect(() => { if (isActive) fetchLeaderboard(); }, [isActive, fetchLeaderboard]);

    useEffect(() => {
        if (!isActive) {
            if (gameRef.current) {
                if (gameRef.current.stop) gameRef.current.stop();
                gameRef.current = null;
            }
            return;
        }

        // Timeout to ensure DOM is ready after AnimatePresence animation
        const timer = setTimeout(() => {
            const canvas = document.getElementById('game');
            console.log('[Terraria] Init attempt: canvas=', !!canvas, 'gameRef=', !!gameRef.current);
            if (!gameRef.current && canvas) {
                try {
                    gameRef.current = new Game();
                    // Connect score submission
                    gameRef.current.onScoreUpdate = (score) => {
                        if (saveScoreRef.current) saveScoreRef.current(score);
                    };
                    console.log('[Terraria] Game initialized successfully');
                } catch (e) {
                    console.error("[Terraria] Game init error:", e);
                }
            } else if (!canvas) {
                console.warn('[Terraria] Canvas #game not found in DOM');
            }
        }, 350);

        return () => {
            clearTimeout(timer);
            if (gameRef.current) {
                if (gameRef.current.stop) gameRef.current.stop();
                gameRef.current = null;
            }
        };
    }, [isActive]);

    return (
        <AnimatePresence>
            {isActive && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                    className="relative w-full h-full bg-black overflow-hidden rounded-xl border border-white/10 shadow-2xl"
                    style={isFullscreen ? { height: '100%' } : { minHeight: '400px' }}
                >
                    <style>{`

        #terraria-container, #terraria-container * {
            box-sizing: border-box;
        }

        #terraria-container {
            width: 100%;
            height: 100%;
            background: #000;
            overflow: hidden;
            position: relative;
        }

        #terraria-container canvas {
            display: block;
            image-rendering: pixelated;
        }

        #game {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
        }

        #ui {
            position: absolute;
            top: 8px;
            left: 8px;
            color: #ccc;
            font-family: monospace;
            font-size: 9px;
            background: rgba(0, 0, 0, .5);
            padding: 4px 7px;
            border-radius: 4px;
            z-index: 10;
            line-height: 1.5;
            pointer-events: none;
            opacity: 0.7;
        }
        #ui #hp { display: none; } /* HP moved to bar */

        /* === HP BAR — top center, juicy === */
        #hp-bar-wrap {
            position: absolute;
            top: 10px;
            left: 50%;
            transform: translateX(-50%);
            width: 220px;
            z-index: 25;
            pointer-events: none;
            font-family: monospace;
        }
        #hp-bar-outer {
            width: 100%;
            height: 16px;
            background: rgba(0,0,0,0.75);
            border: 2px solid rgba(255,255,255,0.15);
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 0 8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05);
            position: relative;
        }
        #hp-bar-fill {
            height: 100%;
            width: 100%;
            background: linear-gradient(90deg, #ff3344, #ff6655, #ff4455);
            border-radius: 6px;
            transition: width 0.2s ease-out;
            position: relative;
            box-shadow: inset 0 2px 4px rgba(255,255,200,0.25), inset 0 -2px 4px rgba(0,0,0,0.2);
        }
        #hp-bar-fill::after {
            content: '';
            position: absolute;
            top: 2px;
            left: 4px;
            right: 4px;
            height: 4px;
            background: linear-gradient(90deg, rgba(255,255,255,0.3), rgba(255,255,255,0.05));
            border-radius: 3px;
        }
        #hp-bar-text {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 16px;
            line-height: 16px;
            text-align: center;
            font-size: 10px;
            font-weight: bold;
            color: #fff;
            text-shadow: 0 1px 3px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.5);
            z-index: 1;
        }
        .hp-bar-hurt {
            animation: hpShake 0.15s ease-out;
        }
        @keyframes hpShake {
            0%, 100% { transform: translateX(-50%); }
            25% { transform: translateX(calc(-50% - 3px)); }
            75% { transform: translateX(calc(-50% + 3px)); }
        }
        .hp-bar-critical #hp-bar-fill {
            animation: hpPulse 0.8s ease-in-out infinite;
        }
        @keyframes hpPulse {
            0%, 100% { filter: brightness(1); }
            50% { filter: brightness(1.4); }
        }
        #hp-bar-dmg {
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            background: rgba(255,255,100,0.35);
            border-radius: 6px;
            transition: width 0.6s ease-out 0.15s;
        }

        /* === XP / STATS BAR — below HP bar === */
        #xp-bar-wrap {
            position: absolute;
            top: 32px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 25;
            pointer-events: none;
            font-family: monospace;
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 9px;
            color: rgba(255,255,255,0.6);
        }
        #xp-display {
            font-weight: bold;
            font-size: 11px;
            color: #44ff88;
            text-shadow: 0 0 4px rgba(68,255,136,0.3);
        }

        #hotbar {
            position: absolute;
            bottom: 10px;
            left: 50%;
            transform: translateX(-50%);
            display: flex;
            gap: 3px;
            z-index: 10
        }

        .slot {
            width: 48px;
            height: 48px;
            border: 2px solid #555;
            background: rgba(0, 0, 0, .7);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 10px;
            color: #fff;
            font-family: monospace;
            position: relative;
            border-radius: 3px;
            cursor: pointer;
            transition: transform .1s, border-color .15s
        }

        .slot.active {
            border-color: #ff0;
            box-shadow: 0 0 10px rgba(255, 255, 0, .5);
            transform: scale(1.08)
        }

        .slot-count {
            position: absolute;
            bottom: 1px;
            right: 3px;
            font-size: 9px;
            color: #ff0
        }

        #minimap {
            position: absolute;
            top: 10px;
            right: 10px;
            border: 2px solid #555;
            z-index: 10;
            border-radius: 3px
        }

        /* === MOBILE TOUCH OVERRIDES === */
        .is-touch-device .touch-controls { display: flex; justify-content: space-between; align-items: flex-end; padding: 0 12px 14px; }
        .is-touch-device #controls { display: none !important; }
        .is-touch-device #minimap { width: 100px !important; height: 70px !important; top: 6px !important; }
        .is-touch-device #ui { font-size: 8px !important; padding: 3px 5px !important; opacity: 0.5 !important; }
        .is-touch-device #hotbar { bottom: 160px !important; }
        .is-touch-device .slot { width: 34px !important; height: 34px !important; }
        .is-touch-device #game { touch-action: none; }
        .is-touch-device #hp-bar-wrap { width: 160px !important; top: 6px !important; }
        .is-touch-device #hp-bar-outer { height: 12px !important; }
        .is-touch-device #hp-bar-text { font-size: 8px !important; height: 12px !important; line-height: 12px !important; }
        .is-touch-device #xp-bar-wrap { top: 22px !important; font-size: 8px !important; }
        .is-touch-device #xp-display { font-size: 9px !important; }

        #boss-bar-wrap {
            position: absolute;
            top: 40px;
            left: 50%;
            transform: translateX(-50%);
            width: 420px;
            z-index: 20;
            display: none;
            text-align: center;
            pointer-events: none
        }

        #boss-name {
            color: #ff4466;
            font-family: monospace;
            font-size: 16px;
            font-weight: bold;
            text-shadow: 0 0 8px #ff0044, 0 0 20px rgba(255, 0, 60, .4);
            margin-bottom: 4px
        }

        #boss-bar-bg {
            width: 100%;
            height: 14px;
            background: rgba(0, 0, 0, .7);
            border: 2px solid #882244;
            border-radius: 3px;
            overflow: hidden;
            box-shadow: 0 0 12px rgba(255, 0, 60, .3)
        }

        #boss-bar-fill {
            height: 100%;
            width: 100%;
            background: linear-gradient(90deg, #ff2244, #ff6644);
            transition: width .15s;
            box-shadow: inset 0 0 6px rgba(255, 255, 200, .3)
        }

        #controls {
            position: absolute;
            bottom: 68px;
            left: 50%;
            transform: translateX(-50%);
            color: #aaa;
            font-family: monospace;
            font-size: 11px;
            text-align: center;
            background: rgba(0, 0, 0, .5);
            padding: 4px 10px;
            border-radius: 4px;
            z-index: 10;
            pointer-events: none
        }

        /* === MOBILE TOUCH CONTROLS === */
        .touch-controls {
            display: none;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 180px;
            z-index: 30;
            pointer-events: none;
        }
        /* Touch device detection via JS class instead of media query */
        .is-touch-device .touch-controls { display: flex; justify-content: space-between; align-items: flex-end; padding: 0 12px 14px; }
        .is-touch-device #controls { display: none !important; }
        .is-touch-device #minimap { width: 110px !important; height: 80px !important; }
        .is-touch-device #ui { font-size: 10px !important; padding: 6px !important; }
        .is-touch-device #hotbar { bottom: 62px !important; }
        .is-touch-device .slot { width: 36px !important; height: 36px !important; }
        .is-touch-device #game { touch-action: none; }
        .touch-joystick-zone {
            width: 140px;
            height: 140px;
            position: relative;
            pointer-events: auto;
            touch-action: none;
        }
        .touch-joystick-base {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 110px;
            height: 110px;
            border-radius: 50%;
            background: rgba(255,255,255,0.03);
            border: 1.5px solid rgba(255,255,255,0.08);
        }
        .touch-joystick-knob {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 44px;
            height: 44px;
            border-radius: 50%;
            background: rgba(255,255,255,0.08);
            border: 1.5px solid rgba(255,255,255,0.15);
            transition: background 0.1s;
        }
        .touch-joystick-knob.active {
            background: rgba(255,255,255,0.15);
            border-color: rgba(255,255,255,0.25);
        }
        /* Right attack joystick — red tint */
        .touch-joystick-zone.attack-zone .touch-joystick-base {
            background: rgba(255,80,80,0.03);
            border-color: rgba(255,80,80,0.1);
        }
        .touch-joystick-zone.attack-zone .touch-joystick-knob {
            background: rgba(255,80,80,0.08);
            border-color: rgba(255,80,80,0.18);
        }
        .touch-joystick-zone.attack-zone .touch-joystick-knob.active {
            background: rgba(255,80,80,0.2);
            border-color: rgba(255,80,80,0.35);
        }
        .touch-joystick-zone.attack-zone .touch-attack-icon {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 16px;
            opacity: 0.25;
            pointer-events: none;
        }
    
                    `}</style>
                    <div className="relative w-full h-full" id="terraria-container">

                        <div id="ui">
                            <div id="hp"></div>
                            <div id="pos">0, 0</div>
                            <div id="biome">Plains</div>
                            <div id="depth">Surface</div>
                            <div id="time-info">Day</div>
                            <div id="mob-count">Mobs: 0</div>
                            <div id="boss-status"></div>
                        </div>

                        {/* HP BAR — top center, juicy */}
                        <div id="hp-bar-wrap">
                            <div id="hp-bar-outer">
                                <div id="hp-bar-dmg" style={{ width: '100%' }}></div>
                                <div id="hp-bar-fill" style={{ width: '100%' }}></div>
                                <div id="hp-bar-text">100 / 100</div>
                            </div>
                        </div>

                        {/* XP / STATS — below HP bar */}
                        <div id="xp-bar-wrap">
                            <span>⛏<span id="stat-blocks">0</span></span>
                            <span>⚔<span id="stat-kills">0</span></span>
                            <span id="xp-display">🏆 <span id="stat-score">0</span> XP</span>
                        </div>

                        {/* EQUIPMENT DISPLAY */}
                        <div id="equip-display" style={{
                            position: 'absolute', top: '68px', left: '50%', transform: 'translateX(-50%)',
                            background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '4px 12px',
                            borderRadius: '8px', fontSize: '11px', fontFamily: 'monospace',
                            zIndex: 15, whiteSpace: 'nowrap', border: '1px solid rgba(255,255,255,0.15)',
                            backdropFilter: 'blur(4px)', pointerEvents: 'none'
                        }}></div>

                        {/* CRAFT NOTIFICATION */}
                        <div id="craft-notify" style={{
                            position: 'absolute', top: '92px', left: '50%', transform: 'translateX(-50%)',
                            background: 'linear-gradient(90deg, rgba(255,200,50,0.8), rgba(255,150,30,0.8))',
                            color: '#fff', padding: '6px 18px', borderRadius: '10px', fontSize: '14px',
                            fontWeight: 'bold', fontFamily: 'monospace', zIndex: 20, whiteSpace: 'nowrap',
                            textShadow: '0 1px 6px rgba(0,0,0,0.5)', display: 'none',
                            boxShadow: '0 4px 16px rgba(255,200,50,0.4)', pointerEvents: 'none'
                        }}></div>

                        {/* ACHIEVEMENT POPUP */}
                        <div id="ach-popup" style={{
                            position: 'absolute', bottom: '80px', left: '50%', transform: 'translateX(-50%)',
                            background: 'linear-gradient(90deg, rgba(255,215,0,0.9), rgba(200,170,0,0.9))',
                            color: '#2a1800', padding: '10px 24px', borderRadius: '12px', fontSize: '15px',
                            fontWeight: 'bold', fontFamily: 'monospace', zIndex: 25, whiteSpace: 'nowrap',
                            textShadow: '0 1px 2px rgba(255,255,255,0.4)', display: 'none',
                            boxShadow: '0 6px 20px rgba(255,215,0,0.5)', border: '2px solid rgba(255,255,255,0.5)',
                            pointerEvents: 'none'
                        }}></div>

                        {/* EVENT NOTIFICATION */}
                        <div id="event-notify" style={{
                            position: 'absolute', top: '55px', left: '50%', transform: 'translateX(-50%)',
                            fontSize: '18px', fontWeight: 'bold', fontFamily: 'monospace', zIndex: 30,
                            whiteSpace: 'nowrap', display: 'none', pointerEvents: 'none',
                            textShadow: '0 0 12px currentColor'
                        }}></div>

                        {/* EVENT STATUS DISPLAY */}
                        <div id="event-display" style={{
                            position: 'absolute', top: '46px', left: '50%', transform: 'translateX(-50%)',
                            fontSize: '10px', fontWeight: 'bold', fontFamily: 'monospace', zIndex: 20,
                            whiteSpace: 'nowrap', display: 'none', pointerEvents: 'none',
                            background: 'rgba(0,0,0,0.5)', padding: '2px 10px', borderRadius: '6px',
                            border: '1px solid rgba(255,255,255,0.1)'
                        }}></div>

                        {/* TRADER CURRENCY */}
                        <div id="trader-currency" style={{
                            position: 'absolute', top: '11px', right: '240px', fontSize: '11px',
                            fontWeight: 'bold', fontFamily: 'monospace', color: '#ffd700', zIndex: 15,
                            background: 'rgba(0,0,0,0.5)', padding: '2px 8px', borderRadius: '4px',
                            pointerEvents: 'none', textShadow: '0 0 4px rgba(255,215,0,0.3)'
                        }}>💰0</div>

                        {/* TRADE PROMPT */}
                        <div id="trade-prompt" style={{
                            position: 'absolute', bottom: '100px', left: '50%', transform: 'translateX(-50%)',
                            fontSize: '13px', fontWeight: 'bold', fontFamily: 'monospace', color: '#ffd700',
                            zIndex: 25, whiteSpace: 'nowrap', display: 'none',
                            background: 'rgba(0,0,0,0.7)', padding: '6px 16px', borderRadius: '8px',
                            border: '1px solid rgba(255,215,0,0.3)', pointerEvents: 'none',
                            textShadow: '0 0 6px rgba(255,215,0,0.3)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
                        }}></div>

                        {/* TRADER SHOP PANEL */}
                        <div id="trader-shop" style={{
                            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                            width: '240px', maxHeight: '320px', overflow: 'auto', display: 'none',
                            background: 'rgba(10,10,20,0.92)', border: '2px solid rgba(255,215,0,0.3)',
                            borderRadius: '12px', padding: '10px', zIndex: 50, fontFamily: 'monospace',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 20px rgba(255,215,0,0.1)',
                            backdropFilter: 'blur(8px)'
                        }}></div>

                        <canvas id="minimap" width="220" height="160"></canvas>
                        <canvas id="game"></canvas>
                        <div id="boss-bar-wrap">
                            <div id="boss-name"></div>
                            <div id="boss-bar-bg">
                                <div id="boss-bar-fill"></div>
                            </div>
                        </div>
                        <div id="controls">WASD: Move | Space: Jump | LClick: Mine/Attack | RClick: Place | 1-8: Slot | E: Inventory | T: Time</div>
                        <div id="hotbar"></div>

                        {/* Mobile touch controls — visible on touch devices */}
                        <div className="touch-controls" id="touch-controls">
                            <div className="touch-joystick-zone" id="touch-joystick">
                                <div className="touch-joystick-base">
                                    <div className="touch-joystick-knob" id="touch-knob"></div>
                                </div>
                            </div>
                            <div className="touch-joystick-zone attack-zone" id="touch-attack-joystick">
                                <div className="touch-joystick-base">
                                    <div className="touch-joystick-knob" id="touch-attack-knob">
                                        <span className="touch-attack-icon">⚔</span>
                                    </div>
                                </div>
                            </div>
                            {/* Mobile inventory button */}
                            <div id="touch-inv-btn" style={{
                                position: 'absolute', bottom: '140px', left: '50%', transform: 'translateX(-50%)',
                                width: '48px', height: '48px', borderRadius: '12px',
                                background: 'rgba(60,60,80,0.7)', border: '2px solid rgba(255,255,255,0.2)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '22px', cursor: 'pointer', zIndex: 30,
                                backdropFilter: 'blur(4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                            }} onClick={() => {
                                if (window._gameRef) {
                                    window._gameRef._invOpen = !window._gameRef._invOpen;
                                    window._gameRef._renderInventory();
                                }
                            }}>📦</div>
                        </div>

                        {/* Leaderboard overlay — top right, below minimap */}
                        <div style={{
                            position: 'absolute', top: '180px', right: '10px', width: '150px', zIndex: 20,
                            pointerEvents: 'none', opacity: 0.7, fontFamily: 'monospace', fontSize: '9px', color: '#ccc'
                        }}>
                            <div style={{ background: 'rgba(0,0,0,0.7)', borderRadius: '4px', padding: '6px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <div style={{ color: '#ff0', fontWeight: 'bold', fontSize: '10px', marginBottom: '3px' }}>🏆 TOP</div>
                                {isLoadingTop ? (
                                    <div style={{ color: '#666' }}>Loading...</div>
                                ) : leaderboard.length > 0 ? (
                                    leaderboard.slice(0, 5).map((entry, idx) => (
                                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1px' }}>
                                            <span style={{ color: idx < 3 ? '#fbbf24' : '#888' }}>{idx + 1}. {entry.name}</span>
                                            <span style={{ color: '#aaa' }}>{entry.score}</span>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ color: '#555' }}>No scores yet</div>
                                )}
                                {isAuthenticated && (
                                    <div style={{ marginTop: '4px', paddingTop: '3px', borderTop: '1px solid rgba(255,255,255,0.1)', color: '#888', fontStyle: 'italic' }}>
                                        YOU: {typeof window !== 'undefined' ? (localStorage.getItem('terraria_best') || 0) : 0}
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>

                    {/* Close Button */}
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 z-[100] p-2 bg-black/50 hover:bg-red-500/50 rounded-lg text-white transition-colors"
                        >
                            ✕
                        </button>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
});

export default Terraria2Game;